import { Account, AccountADFormValues, ADRestoration } from '@/client/accounts';
import { SystemRoles } from '@/client/users';
import {
  CLOSED_USER_CREATION_WARNING_SYNC,
  LoadingStateType,
} from '@/common/constants';
import { CYBERPEDIA_CLIENT_SECRET_UPDATE_URL } from '@/common/constants/external-urls';
import { DialogContext } from '@/common/context';
import { ADRestorationsConfirmDialog } from '@/components/accounts/ADRestorationsConfirmDialog';
import { azureAdSchema } from '@/components/accounts/forms/validations/azure-ad';
import { FormDirtyTracker, FormikInput } from '@/components/form';
import { CopyIconButton } from '@/components/templates/CopyIconButton';
import { usePlanners } from '@/hooks/query/planner.hooks';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentUser } from '@/store/features/users';
import { DialogType } from '@/system-settings/enums';
import { AppButton } from '@/ui/buttons';
import { FlexContainer, FormContainer } from '@/ui/styled-ui';
import { isDemoRestrictedMode } from '@/utils/helpers';
import { Field, Form, Formik } from 'formik';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledUpdateSecretWrapper = styled.div`
  padding-top: var(--default-padding);
  flex-shrink: 0;
`;

type AzureADFormProps = {
  initialValues: AccountADFormValues;
  onSubmit: (data: AccountADFormValues) => void;
  onSync: () => void;
  account: Account;
  currentClientSecret?: string;
  syncState?: LoadingStateType;
  disabled?: boolean;
  restorations?: ADRestoration[];
  showRestorationsDialog?: boolean;
  onRestorationsConfirm?: () => void;
  onOverwriteConfirm?: () => void;
  onRestorationsClose?: () => void;
  secretNeedsUpdate?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  submitRef?: React.MutableRefObject<(() => Promise<boolean | void>) | null>;
};
export const AzureADForm: React.FC<AzureADFormProps> = ({
  initialValues,
  onSubmit,
  account,
  currentClientSecret,
  disabled,
  restorations = [],
  showRestorationsDialog = false,
  onRestorationsConfirm,
  onOverwriteConfirm,
  onRestorationsClose,
  secretNeedsUpdate = false,
  onDirtyChange,
  submitRef,
}) => {
  const { t } = useTranslation();
  const { setDialogData } = useContext(DialogContext);
  const [passwordVisibility, setPasswordVisibility] = useState(false);
  const { planners } = usePlanners(
    {
      skip: 0,
      take: 0,
    },
    account?.id,
  );
  const currentUser = useAppSelector(selectCurrentUser);
  const isDemoAccount = isDemoRestrictedMode(account, currentUser);

  const handleAdSubmission = (data: AccountADFormValues) => {
    if (isDemoAccount) return;
    const isShown =
      !localStorage.getItem(CLOSED_USER_CREATION_WARNING_SYNC) &&
      planners?.count;
    if (isShown) {
      setDialogData({
        show: true,
        type: DialogType.WARNING,
        header: t('dialog.enrollment.warning.heading'),
        message: t('dialog.enrollment.warning'),
        showDialogConfirmation: true,
        confirmationMessage: t('dialog.enrollment.doNotShow'),
        confirmationConst: CLOSED_USER_CREATION_WARNING_SYNC,
        onAccept: () => onSubmit(data),
      });
    } else {
      onSubmit(data);
    }
  };

  return (
    <>
      <ADRestorationsConfirmDialog
        restorations={restorations}
        visible={showRestorationsDialog}
        onConfirm={onRestorationsConfirm ?? (() => undefined)}
        onOverwrite={onOverwriteConfirm ?? (() => undefined)}
        onClose={onRestorationsClose ?? (() => undefined)}
      />

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={azureAdSchema(!!initialValues?.hasClientSecret, t)}
        onSubmit={handleAdSubmission}
      >
        {({ values }) => {
          return (
            <FormContainer width={100}>
              <Form className="w-full">
                <FormDirtyTracker
                  onDirtyChange={onDirtyChange}
                  submitRef={submitRef}
                />
                <FlexContainer
                  gap={40}
                  width="100%"
                  justify="space-between"
                  align="flex-start"
                  wrap="wrap"
                >
                  <div className="flex-1">
                    <div className="field w-full">
                      <Field
                        id="clientId"
                        name="clientId"
                        label={t('azure.clientId')}
                        className="w-full"
                        value={values.clientId}
                        component={FormikInput}
                        required
                        disabled={disabled}
                      />
                    </div>
                    <FlexContainer align="flex-start" gap={28}>
                      <FlexContainer align="flex-start" className="w-full">
                        <div className="field w-full">
                          <Field
                            id="clientSecret"
                            name="clientSecret"
                            placeholder={
                              values.hasClientSecret
                                ? t('azure.clientSecret.set')
                                : undefined
                            }
                            label={t('azure.clientSecret')}
                            className="w-full"
                            value={undefined}
                            component={FormikInput}
                            required
                            disabled={disabled}
                            type={passwordVisibility ? 'text' : 'password'}
                          />
                        </div>
                        <AppButton
                          icon={
                            passwordVisibility ? 'pi pi-eye' : 'pi pi-eye-slash'
                          }
                          type="text"
                          severity="secondary"
                          onClick={() =>
                            setPasswordVisibility((state) => !state)
                          }
                          className="input-eye-button"
                          isDisabled={
                            values.hasClientSecret && !values.clientSecret
                          }
                        />
                        {currentUser?.role?.code === SystemRoles.DEVELOPER &&
                          currentClientSecret && (
                            <CopyIconButton
                              clipboardValue={currentClientSecret}
                              className="input-eye-button"
                            />
                          )}
                      </FlexContainer>
                      {secretNeedsUpdate && (
                        <StyledUpdateSecretWrapper>
                          <AppButton
                            label={t('azure.clientSecret.update')}
                            type="outlined"
                            severity="secondary"
                            onClick={() =>
                              window.open(
                                CYBERPEDIA_CLIENT_SECRET_UPDATE_URL,
                                '_blank',
                                'noopener,noreferrer',
                              )
                            }
                          />
                        </StyledUpdateSecretWrapper>
                      )}
                    </FlexContainer>
                    <div className="field w-full">
                      <Field
                        id="tenantId"
                        name="tenantId"
                        label={t('azure.tenantId')}
                        className="w-full"
                        value={values.tenantId}
                        component={FormikInput}
                        required
                        disabled={disabled}
                      />
                    </div>
                  </div>
                </FlexContainer>
              </Form>
            </FormContainer>
          );
        }}
      </Formik>
    </>
  );
};
